import React from 'react';
import styled from 'styled-components';

import Button from '@minecraft.button';
import { BaseballCard } from '@minecraft.molecules';
import { DetailTextLarge } from '@minecraft.atoms';
import { TalentScoutProfileFragment } from '@minecraft.graphql-operations';
import { useUserContext } from '@blocs.auth';
import { useTranslation } from '@blocs.i18n';

import { ContactTalentModal } from './ContactTalentModal';

const ContactTalentButton = (props: { openModal: () => void }) => {
  const { t } = useTranslation();

  return (
    <BaseballCard.Section size="small">
      <Button
        className="cn_w-full"
        data-testid="talent-scout-search-profile-contact-button"
        isSecondary
        onClick={props.openModal}
      >
        {t('common:label.contact')}
      </Button>
    </BaseballCard.Section>
  );
};

const ContactAgainButton = styled.button`
  background-color: transparent;
  border: none;
  color: inherit;
  display: inline;
  padding: 0;
  text-decoration: underline;
`;

const ContactAgain = (props: { lastContacted: Date; openModal: () => void }) => {
  const { t, formatIntlDate } = useTranslation();
  const { myAccount } = useUserContext();
  const lastContactedStr = props?.lastContacted
    ? formatIntlDate({
        date: props.lastContacted?.toISOString(),
        format: 'MONTH_LONG_DAY_YEAR',
        replaceUtc: false,
        timeZoneStandardName: myAccount?.timeZone?.standardName,
      })
    : '';

  return (
    <BaseballCard.Section backgroundColor="logoPlaceholder" size="medium">
      <DetailTextLarge color="panelHeader" lineHeight="ultraCondensed">
        {t('reps:talentScout.contact.contactedOn', { date: lastContactedStr })}
        <br />
        <ContactAgainButton onClick={props.openModal}>{t('common:label.contactAgain')}</ContactAgainButton>
      </DetailTextLarge>
    </BaseballCard.Section>
  );
};

export const getLastContacted = (contactHistory: TalentScoutProfileFragment['contactHistory']) =>
  contactHistory
    ?.map(({ createdAt }) => new Date(createdAt).valueOf())
    .sort((a, b) => b - a)
    .map((date) => new Date(date))[0];

type ContactTalentProps = Pick<TalentScoutProfileFragment, 'contactHistory' | 'profileId' | 'stageName'>;

export const ContactTalent = ({ contactHistory, profileId, stageName }: ContactTalentProps) => {
  const [showModal, setShowModal] = React.useState(false);
  const lastContacted = React.useMemo(() => getLastContacted(contactHistory), [contactHistory]);

  return (
    <>
      <ContactTalentModal
        showModal={showModal}
        setShowModal={setShowModal}
        profileId={profileId}
        stageName={stageName}
      />
      {lastContacted ? (
        <ContactAgain lastContacted={lastContacted} openModal={() => setShowModal(true)} />
      ) : (
        <ContactTalentButton openModal={() => setShowModal(true)} />
      )}
    </>
  );
};
